import React, { useRef } from "react";

import {
  Form as FormikForm,
  Formik,
  FormikErrors,
  FormikHelpers,
  FormikProps,
  useFormikContext,
} from "formik";
import isEqual from "lodash/isEqual";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { AdminLink } from "components/AdminLink/AdminLink";
import AuditLogLink from "components/AuditLog/AuditLogLink";
import { ClickableCommentIcon } from "components/Comments/Icon";
import { LegacyVendorSplitCollapse } from "components/DetailedSaleLotModal/LegacyVendorSplitCollapse";
import { VendorSplitCollapse } from "components/DetailedSaleLotModal/VendorSplitCollapse";
import { Button, SecondaryButton } from "components/Form";
import { CheckBox } from "components/Form/FormikControls";
import { IntegrationIcon } from "components/Icons";
import { IntegrationCollapse } from "components/IntegrationCollapse";
import { Row } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";
import { MediaCollapse } from "components/Media/MediaCollapse";
import { CombinedMissingFieldIndicator } from "components/MissingFieldsIndicator";
import { CurrentSaleReadOnly } from "components/ReadOnly";

import { AuditLogTypes } from "constants/auditLog";
import { ClearingSaleLotStatus } from "constants/clearingSaleAttributes";
import { CommentType } from "constants/comments";
import { ApiModel } from "constants/loading";
import { SaleLotModalSection } from "constants/navigation";
import { SaleLotPermission } from "constants/permissions";
import { SaleType } from "constants/sale";

import {
  ForClearingSale,
  ForLivestockAuction,
  ForLivestockSale,
  ForNotClearingSale,
  ForNotHooks,
} from "containers/ForSaleType";

import { calculateTotalPriceCents } from "lib";

import { getBuyerInformationString } from "lib/businesses";
import { deepObjectChanges } from "lib/compare";
import { getCombinedLotNumber } from "lib/saleLot";

import {
  getBidders,
  getConsignmentById,
  getHasWriteAccessInCurrentSale,
  getIsConsigningAgent,
  getIsConsigningAgentAndSaleUnlocked,
  getSaleLotById,
  getVendorSplitSaleLotIdsByParentSaleLotId,
  selectBidderByBuyerInformation,
  selectBidderIdBySaleLotIdLookup,
  selectCurrentMixedBreedId,
} from "selectors";

import { useAdvancedDraftingMirrorEffects } from "hooks";
import {
  useHasPermission,
  useHasVendorSplitPermission,
} from "hooks/useHasPermission";

import { ActionsCollapse } from "./ActionsCollapse";
import { AdvancedDraftingCollapse } from "./AdvancedDrafting";
import ClearingSaleSaleLotPickupDetails from "./ClearingSaleSaleLotPickupDetails";
import { getDetailedSaleLotValidationSchema } from "./detailedSaleLotValidationSchema";
import EIDsCollapse from "./EIDsCollapse";
import GeneralCollapse from "./GeneralCollapse";
import { ConsignmentSectionHeader } from "./Header";
import { PenCollapse } from "./PenCollapse";
import SaleCollapse from "./SaleCollapse";
import ScanningCollapse from "./ScanningCollapse";
import { validateAdvancedDrafting } from "lib/saleLot/validateAdvancedDrafting";

// Sale.rounds is typed as number[] in SaleBase, but at runtime the selector
// returns full round objects with an `id` field.
// TODO: fix rounds: number[] → Array<{ id: number }> in state.d.ts SaleBase so this local override can go away.
interface FormSale extends Omit<Sale, "rounds"> {
  rounds: Array<{ id: number }>;
}

interface FormConsignment {
  deployment_sale: number;
  agencyId?: string;
}

// Form values extend Partial<SaleLot> with extra form-only and runtime-added fields.
interface SaleLotFormValues extends Partial<SaleLot> {
  bidderNumber: number | null;
  buyer?: Record<string, unknown>;
  consignment?: { vendor_property_id?: string | null };
  createAnother: boolean;
  unit_price?: string | number;
  useConsignmentPickupDetails: boolean;
}

type CommonFormProps = Pick<
  FormikProps<SaleLotFormValues>,
  | "errors"
  | "handleBlur"
  | "handleChange"
  | "setFieldTouched"
  | "setFieldValue"
  | "setTouched"
  | "touched"
  | "values"
>;

interface FormProps {
  action: string;
  changeSection: (currentSection: string, newSection: string) => void;
  closeSelf: () => void;
  consignment: FormConsignment;
  deleteSaleLot?: () => void;
  hasChangeSaleLotPermission: boolean;
  initialValues: Partial<SaleLotFormValues>;
  onAfterClose?: (id: string | null, createAnother: boolean) => void;
  onBeforeClose?: (id: string | null) => void;
  rounds?: Array<{ id: number }>;
  sale: FormSale;
  saleLotId?: string;
  section: string;
  showCreateAnother: boolean;
  speciesAttributes?: Record<string, unknown>;
}

interface DetailedSaleLotModalProps
  extends Omit<FormProps, "hasChangeSaleLotPermission" | "rounds"> {
  addBidderRegistration: (
    id: string,
    data: {
      registrationNumber: number;
      businessId: string;
      defaultPropertyId: string | null;
      buyerWay: string | undefined;
    },
  ) => void;
  addSaleLotWithPens: (id: string, patch: Record<string, unknown>) => void;
  consignmentId: string;
  patchSaleLot: (
    patch: Record<string, unknown>,
    options: { changeReason: string },
  ) => void;
}

const isNew = (initialValues: Partial<SaleLotFormValues>): boolean =>
  !("id" in initialValues);

const alwaysEditableFields = [
  "invoiceToBusinessId",
  "dentitionId",
  "estimatedAverageWeightId",
  "isGSTExempt",
  "labels",
];

const Form = ({
  section,
  changeSection,
  action,
  closeSelf,
  consignment,
  deleteSaleLot,
  initialValues,
  onAfterClose,
  onBeforeClose,
  saleLotId,
  showCreateAnother,
  speciesAttributes,
  rounds,
  sale,
  hasChangeSaleLotPermission,
}: FormProps): React.JSX.Element => {
  const {
    errors,
    handleBlur,
    handleChange,
    isValid,
    setTouched,
    setFieldTouched,
    setFieldValue,
    submitCount,
    touched,
    values,
  } = useFormikContext<SaleLotFormValues>();

  // TODO - set defaults for accreditations.
  const {
    consignment_id: selectedConsignmentId,
    quantity,
    quantityProgeny,
  } = values;

  const status = values.clearingSaleAttributes?.status;

  const selectedConsignment = useSelector(
    getConsignmentById(selectedConsignmentId),
  );

  const hasWriteAccessInCurrentSale = useSelector(
    state =>
      getHasWriteAccessInCurrentSale(state) ||
      getIsConsigningAgentAndSaleUnlocked(state),
  );

  const isConsigningAgent = useSelector(getIsConsigningAgent);

  const lockedForConsigningAgents =
    sale.sale_type === SaleType.CLEARING
      ? isConsigningAgent && status !== ClearingSaleLotStatus.PENDING
      : isConsigningAgent;

  const isReadOnly =
    !hasWriteAccessInCurrentSale ||
    (!isNew(initialValues) && !hasChangeSaleLotPermission) ||
    lockedForConsigningAgents;

  const hasVendorSplitsPermission = useHasVendorSplitPermission();

  const vendorSplitSaleLotIds = useSelector(
    getVendorSplitSaleLotIdsByParentSaleLotId(saleLotId),
  );

  // Allow submission if:
  //  - we have write access
  //  - it's a consigning agent creating
  //  - or editing a PENDING sale lot.
  //  - ONLY specific fields have been touched.

  const onlyWritableFieldsTouched =
    Object.keys(touched).length > 0 &&
    Object.keys(touched).every(touchedField =>
      alwaysEditableFields.includes(touchedField),
    );

  const allowSubmit =
    hasWriteAccessInCurrentSale ||
    (sale.sale_type === SaleType.CLEARING &&
      isConsigningAgent &&
      (status === ClearingSaleLotStatus.PENDING || !saleLotId)) ||
    (onlyWritableFieldsTouched && hasChangeSaleLotPermission);

  useAdvancedDraftingMirrorEffects();

  // change the main breed by the advanced drafting 1
  const onChangeBreed = (field: string | number | null): void => {
    setFieldValue("breed_id", field, true);
  };

  const toggleSection = (sectionName: string) => () => {
    changeSection(section, sectionName);
  };

  const commonProps: CommonFormProps = {
    values,
    touched,
    errors,
    setFieldTouched,
    setFieldValue,
    handleChange,
    handleBlur,
    setTouched,
  };

  const deploymentSaleId =
    selectedConsignment?.deployment_sale || consignment.deployment_sale;
  const deploymentId = sale.deployment_sales?.find(
    ds => ds.deployment_sale_id === deploymentSaleId,
  )?.deployment_id;

  const onClose = (): void => {
    onBeforeClose && onBeforeClose(null);
    closeSelf();
    onAfterClose && onAfterClose(null, false);
  };

  return (
    <ZoomyDialog open onClose={onClose} fullWidth maxWidth={undefined}>
      <FormikForm data-tour="salelot-modal">
        <DialogTitle onClose={onClose}>
          <Row justifyBetween alignCenter>
            <Row alignCenter>
              {saleLotId && (
                <AuditLogLink
                  auditLogType={AuditLogTypes.SALE_LOT}
                  dataId={saleLotId}
                  returnTo={window.location.hash}
                />
              )}
              &nbsp;
              {isNew(initialValues)
                ? "Create Lot"
                : `Edit Lot ${getCombinedLotNumber(values) || ""}`}
              <AdminLink model="salelot" cbid={saleLotId} />
            </Row>
            <Row>
              <CombinedMissingFieldIndicator
                modelIds={saleLotId ? [saleLotId] : []}
              />
              <IntegrationIcon saleLotValues={values} />
              {saleLotId ? (
                <ClickableCommentIcon
                  commentType={CommentType.SALE_LOT}
                  commentTypeId={saleLotId}
                  returnTo={window.location.hash}
                />
              ) : null}
            </Row>
          </Row>
        </DialogTitle>
        <DialogContent dividers form>
          <ConsignmentSectionHeader readOnly={isReadOnly} />

          <GeneralCollapse
            isOpen={section === SaleLotModalSection.GENERAL}
            onToggle={toggleSection(SaleLotModalSection.GENERAL)}
            speciesAttributes={speciesAttributes}
            isNew={isNew(initialValues)}
            deleteSaleLot={deleteSaleLot}
            closeSelf={onClose}
            rounds={rounds}
            deploymentId={deploymentId}
            {...commonProps}
            readOnly={isReadOnly}
            hasChangeSaleLotPermission={hasChangeSaleLotPermission}
          />

          <ForNotClearingSale>
            <AdvancedDraftingCollapse
              isOpen={section === SaleLotModalSection.INTERFACES}
              onToggle={toggleSection(SaleLotModalSection.INTERFACES)}
              saleLot={values}
              commonProps={commonProps}
              onChangeBreed={onChangeBreed}
              readOnly={isReadOnly}
            />
          </ForNotClearingSale>
          <ForClearingSale>
            <ClearingSaleSaleLotPickupDetails
              consignmentId={selectedConsignmentId}
              isOpen={section === SaleLotModalSection.PICKUP_DETAILS}
              onToggle={toggleSection(SaleLotModalSection.PICKUP_DETAILS)}
            />
          </ForClearingSale>

          <ForLivestockAuction>
            <PenCollapse
              isOpen={section === SaleLotModalSection.AUCTION_PEN}
              onToggle={toggleSection(SaleLotModalSection.AUCTION_PEN)}
              commonProps={commonProps}
              action={action}
              readOnly={isReadOnly}
            />
          </ForLivestockAuction>
          <ForLivestockSale>
            {!isNew(initialValues) && (
              <ScanningCollapse
                isOpen={section === SaleLotModalSection.SCANNING}
                onToggle={toggleSection(SaleLotModalSection.SCANNING)}
                saleLotId={initialValues.id}
                quantity={quantity}
                quantityProgeny={quantityProgeny}
                readOnly={isReadOnly}
              />
            )}
          </ForLivestockSale>
          <SaleCollapse
            deploymentSaleId={consignment.deployment_sale}
            isOpen={section === SaleLotModalSection.SELLING}
            onToggle={toggleSection(SaleLotModalSection.SELLING)}
            quantity={values.quantity}
            readOnly={isReadOnly}
          />

          {!isNew(initialValues) && (
            <>
              <ForNotHooks>
                <MediaCollapse
                  isOpen={section === SaleLotModalSection.MEDIA}
                  onToggle={toggleSection(SaleLotModalSection.MEDIA)}
                  saleLotId={initialValues.id}
                  saleType={sale.sale_type}
                  readOnly={isReadOnly}
                />
              </ForNotHooks>

              <ForClearingSale>
                <IntegrationCollapse
                  isOpen={section === SaleLotModalSection.INTEGRATIONS}
                  onToggle={toggleSection(SaleLotModalSection.INTEGRATIONS)}
                  consignmentId={selectedConsignmentId}
                />
              </ForClearingSale>

              <ForLivestockSale>
                <EIDsCollapse
                  isOpen={section === SaleLotModalSection.EIDS}
                  onToggle={toggleSection(SaleLotModalSection.EIDS)}
                  saleLotId={saleLotId}
                  vendorPropertyId={
                    initialValues.consignment?.vendor_property_id
                  }
                  readOnly={isReadOnly}
                />
                {hasVendorSplitsPermission &&
                  vendorSplitSaleLotIds.length > 0 && (
                    <LegacyVendorSplitCollapse
                      isOpen={
                        section === SaleLotModalSection.LEGACY_VENDOR_SPLITS
                      }
                      onToggle={toggleSection(
                        SaleLotModalSection.LEGACY_VENDOR_SPLITS,
                      )}
                      saleLotId={saleLotId}
                    />
                  )}

                {hasVendorSplitsPermission && (
                  <VendorSplitCollapse
                    isOpen={section === SaleLotModalSection.VENDOR_SPLITS}
                    onToggle={toggleSection(SaleLotModalSection.VENDOR_SPLITS)}
                    saleLotId={saleLotId}
                  />
                )}
              </ForLivestockSale>
              <CurrentSaleReadOnly>
                <WaitForSync requiredData={[ApiModel.SALE_LOTS]}>
                  <ActionsCollapse
                    isOpen={section === SaleLotModalSection.ACTIONS}
                    onToggle={toggleSection(SaleLotModalSection.ACTIONS)}
                    toggleEidsSection={toggleSection(SaleLotModalSection.EIDS)}
                    saleLotId={saleLotId}
                    onClose={onClose}
                    readOnly={isReadOnly}
                  />
                </WaitForSync>
              </CurrentSaleReadOnly>
            </>
          )}

          {showCreateAnother && (
            <Row justifyEnd alignEnd style={{ marginTop: 20 }}>
              <CheckBox name="createAnother" label="Create another" />
            </Row>
          )}
        </DialogContent>
        <DialogActions>
          <SecondaryButton data-tour="cancel" type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          {allowSubmit ? (
            <Button
              data-tour="submit"
              type="submit"
              disabled={
                (submitCount > 0 && !isValid) ||
                (!isNew(initialValues) && !isValid)
              }
            >
              {sale.sale_type === SaleType.CLEARING && isNew(initialValues)
                ? "Save & Continue"
                : "Submit"}
            </Button>
          ) : null}
        </DialogActions>
      </FormikForm>
    </ZoomyDialog>
  );
};

const DetailedSaleLotModal = (
  props: DetailedSaleLotModalProps,
): React.JSX.Element => {
  const {
    addSaleLotWithPens,
    closeSelf,
    consignmentId,
    initialValues,
    onBeforeClose,
    onAfterClose,
    patchSaleLot,
    addBidderRegistration,
    sale,
    section,
    changeSection,
    saleLotId,
  } = props;

  const initialValuesRef = useRef(initialValues);

  const bidderIdBySaleLotIdLookup = useSelector(
    selectBidderIdBySaleLotIdLookup,
  );

  const bidderLookup = useSelector(getBidders);

  const bidderId = bidderIdBySaleLotIdLookup[saleLotId];

  const bidder = bidderLookup[bidderId];

  const bidderByBuyerInformationLookup = useSelector(
    selectBidderByBuyerInformation,
  );

  const hasChangeSaleLotPermission = useHasPermission(
    getSaleLotById(saleLotId),
    SaleLotPermission.update,
  );

  const openAdvancedDraftingSection = (): void => {
    if (section !== SaleLotModalSection.INTERFACES) {
      changeSection(section, SaleLotModalSection.INTERFACES);
    }
  };

  const mixedBreedId = useSelector(selectCurrentMixedBreedId);

  const handleSubmit = (
    values: SaleLotFormValues,
    { setErrors, setSubmitting }: FormikHelpers<SaleLotFormValues>,
  ): void => {
    if (!isNew(initialValues) && !hasChangeSaleLotPermission) {
      return;
    }

    if (sale.sale_type !== SaleType.CLEARING) {
      const draftingErrors = validateAdvancedDrafting(values, mixedBreedId);
      if (draftingErrors) {
        // AdvancedDraftingErrors is a subset of FormikErrors — safe to cast
        setErrors(draftingErrors as FormikErrors<SaleLotFormValues>);
        openAdvancedDraftingSection();
        setSubmitting(false);
        return;
      }
    }

    if (
      sale.using_registered_bidders &&
      values.bidderNumber &&
      values.bidderNumber !== initialValues.bidderNumber
    ) {
      const buyerInformationString = getBuyerInformationString(
        values.buyer_id,
        values.destination_property_id,
        values.buyer_way?.name,
      );

      const associatedBidder =
        bidderByBuyerInformationLookup[buyerInformationString];

      if (!associatedBidder) {
        // if a bidder does not exist for the current buyer/way/pic combination, create one
        addBidderRegistration(uuidv4(), {
          registrationNumber: values.bidderNumber,
          businessId: values.buyer_id,
          defaultPropertyId: values.destination_property_id,
          buyerWay: values?.buyer_way?.name,
        });
      }
    }

    // Pull the values out into a provisional patch, stripping the buyer relation.
    const { buyer: buyerIgnored, ...patch } = { ...values };

    // Munge the price back together.
    patch.total_price_cents = calculateTotalPriceCents({
      unitPrice: values.unit_price ? parseFloat(String(values.unit_price)) : 0,
      quantity: values.quantity,
      pricing_type_id: values.pricing_type_id,
      total_mass_grams: values.total_mass_grams,
    });

    if (isNew(initialValues)) {
      patch.created = new Date().toUTCString();
      const newSaleLotId = uuidv4();
      addSaleLotWithPens(newSaleLotId, patch);

      onBeforeClose && onBeforeClose(newSaleLotId);
      closeSelf();
      onAfterClose && onAfterClose(newSaleLotId, values.createAnother);
    } else {
      // Compare what we've actually changed, to what was there when we opened the form (note, this is potentially
      // different to what's in state, too!)
      const { buyer: buyerIgnored2, ...initialValuesWithoutBuyer } =
        initialValuesRef.current;
      const deepPatch = deepObjectChanges(initialValuesWithoutBuyer, patch, [
        [
          "total_mass_grams",
          "quantity",
          "total_price_cents",
          "pricing_type_id",
        ],
        ["buyer_id", "buyer_way"],
        ["location", "color"],
      ]);

      // The deepObjectChanges algorithm is too general to handle the intricacies of all the fields on a Sale Lot,
      // manage all the misses here.  Non simple fields (objects/arrays) that require the whole object if any part of it has changed.
      const complexFields = ["exportSites", "auction_pen", "deliveryPen"];
      complexFields.forEach(fieldName => {
        if (!isEqual(initialValuesWithoutBuyer[fieldName], patch[fieldName])) {
          deepPatch[fieldName] = patch[fieldName];
        }
      });

      // Use a purely replace algorithm for Labels
      if (
        Array.isArray(patch.labels) &&
        patch.labels !== initialValuesWithoutBuyer.labels
      ) {
        deepPatch.labels = patch.labels;
      }

      patchSaleLot(
        { id: initialValues.id, ...deepPatch },
        { changeReason: "Updated from edit sale lot" },
      );

      onBeforeClose && onBeforeClose(values.id);
      closeSelf();
      onAfterClose && onAfterClose(values.id, false);
    }
  };

  const getInitialValues = (): SaleLotFormValues => {
    // Required fields need to go in initialValues or they will not have visible
    // errors until they are touched by the user.

    const requiredFields: Partial<SaleLotFormValues> = {
      quantity: 0,
      sale_round_id: -1,
    };

    if (sale.sale_type === SaleType.CLEARING) {
      requiredFields.clearingSaleAttributes = {
        status: ClearingSaleLotStatus.PENDING,
      } as ClearingSaleAttributes;
    }

    // Auto select the round if there is only one.
    if (sale.rounds.length === 1) {
      requiredFields.sale_round_id = sale.rounds[0].id;
    }

    const lotValues: SaleLotFormValues = {
      bidderNumber: bidder ? bidder.registrationNumber : null,
      createAnother: false,
      useConsignmentPickupDetails: initialValues.pickupAddress === null,
      consignment_id: consignmentId,
      buyer_id: sale.default_buyer_id,
      destination_property_id: sale.default_property_id,
      ...requiredFields,
      ...initialValues,
    };

    if (sale.sale_type === SaleType.CLEARING) {
      lotValues.clearingSaleAttributes = {
        ...requiredFields.clearingSaleAttributes,
        ...initialValues.clearingSaleAttributes,
      } as ClearingSaleAttributes;
    }
    return lotValues;
  };

  const validationSchema = getDetailedSaleLotValidationSchema(sale.sale_type);
  const formInitialValues = getInitialValues();

  return (
    <Formik
      key={formInitialValues.id}
      onSubmit={handleSubmit}
      initialValues={formInitialValues}
      validationSchema={validationSchema}
    >
      <Form
        {...props}
        hasChangeSaleLotPermission={hasChangeSaleLotPermission}
      />
    </Formik>
  );
};

export default DetailedSaleLotModal;
